import React, { useEffect, useState } from "react";
import {
  Field,
  makeStyles,
  shorthands,
  Toaster,
} from "@fluentui/react-components";
import apiServices from "../../../service";
import { Category, Report } from "../../../types/reportTypes";
import ReportSelector from "../../DynamicReport/components/ReportSelector";
import ReportForm from "../../DynamicReport/components/ReportForm";
import OverlayLoader from "../../../components/OverlayLoader";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import moment from "moment";
import {
  CLIENT_LIST_REPORT_PARAM,
  CLIENT_LIST_PARAM_LABEL,
  buildClientContextCategories,
} from "./clientReportsUtils";

const useStyles = makeStyles({
  root: { ...shorthands.padding("16px") },
  container: { display: "flex", gap: "10px", flexWrap: "wrap" },
  formField: { display: "flex", alignItems: "baseline", gap: "10px" },
});

interface ClientReportsProps {
  activeCustomerId?: string;
  notify?: (message?: any, type?: any) => void;
}

function ClientReports({ activeCustomerId }: ClientReportsProps) {
  const styles = useStyles();
  const { showToast, toasterId } = useToastMessage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    apiServices.getReport
      .getList()
      .then((res: any) => {
        if (!res.data.isError && res.data) {
          // Keep only the reports that declare a "Client List" parameter,
          // recursing through however many levels of sub-category nesting
          // they live under; drop any (sub-)category left with zero reports
          // afterwards. Not a CategoryCode check, and not limited to one level.
          setCategories(buildClientContextCategories(res.data));
        } else {
          setError("Failed to load report list");
        }
      })
      .catch(() => {
        setError("An error occurred while fetching the report list");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSelectReport = (report: Report) => {
    setSelectedReport({
      ...report,
      // --- customerId-parameter stripping step (same helper/key as the filter above) ---
      // "Client List" must never be rendered here — the client is already known
      // from the route, not a user selection.
      parameters: (report.parameters || []).filter(
        (p) =>
          p.reportParam !== CLIENT_LIST_REPORT_PARAM &&
          p.label !== CLIENT_LIST_PARAM_LABEL,
      ),
    });
  };

  const handleReportGenerated = () => {
    setSelectedReport(null);
  };

  const fixedParams: Record<string, any> = {
    customerId: activeCustomerId ?? "",
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div
        className={styles.root}
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <OverlayLoader isLoading={isLoading} />
        {error && <div style={{ color: "red" }}>{error}</div>}

        <div className={styles.container}>
          <div className={styles.formField}>
            <Field label="Select a Report">
              <ReportSelector
                categories={categories}
                selectedReport={selectedReport}
                onSelectReport={handleSelectReport}
              />
            </Field>
          </div>

          {selectedReport && (
            <ReportForm
              selectedReport={selectedReport}
              onReportGenerated={handleReportGenerated}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              fixedParams={fixedParams}
              showToast={(title, message, type) =>
                showToast(
                  title,
                  message,
                  type as "success" | "error" | "warning",
                )
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

export default ClientReports;
